import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  X,
  Key,
  Mic,
  Image,
  Sliders,
  CheckCircle2,
  Radio,
  Server,
  ShieldCheck,
  Cpu,
  RefreshCw,
} from 'lucide-react';
import {
  GoogleGenAIConfig,
  GoogleGeminiVoice,
  loadAIConfig,
  saveAIConfig,
  getNarrationController,
  generateArtworkWithGoogleAI,
} from '../../services/googleGenAI';

export interface GoogleGenAIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GEMINI_VOICES: { id: GoogleGeminiVoice; name: string; role: string }[] = [
  { id: 'Fenrir', name: 'Fenrir', role: 'Marcus Ward — Deep, Gravelly Veteran' },
  { id: 'Charon', name: 'Charon', role: 'Somber Interstellar Void' },
  { id: 'Puck', name: 'Puck', role: 'Silas Vance — Restless Junior Tech' },
  { id: 'Aoede', name: 'Aoede', role: 'Elena Cruz — Measured Architect' },
  { id: 'Kore', name: 'Kore', role: 'Dr. Simon Peter — Analytical Clinical' },
];

export const GoogleGenAIModal: React.FC<GoogleGenAIModalProps> = ({ isOpen, onClose }) => {
  const [config, setConfig] = useState<GoogleGenAIConfig>(loadAIConfig);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [isTestingImage, setIsTestingImage] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setConfig(loadAIConfig());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    saveAIConfig(config);
    setTestStatus('Settings saved successfully.');
    setTimeout(() => setTestStatus(null), 2500);
  };

  const handleTestVoice = () => {
    const controller = getNarrationController();
    controller.narratePage(
      [
        'The Caesura is not an empty margin. Between tick N and tick N plus one, something cold, geometric, and patient watches while the universe catches its breath.',
      ],
      config
    );
  };

  const handleTestImage = async () => {
    setIsTestingImage(true);
    setTestStatus('Generating test artwork via active provider...');
    try {
      const img = await generateArtworkWithGoogleAI(
        'Vast Planck ignition gate tearing space, cerulean coronal discharge',
        999,
        config
      );
      if (img) {
        setTestStatus('Image generated successfully! Cached for Page 999.');
      }
    } catch (err: any) {
      setTestStatus(`Generation warning: ${err?.message || 'Check proxy/keys'}`);
    } finally {
      setIsTestingImage(false);
      setTimeout(() => setTestStatus(null), 4000);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-card ai-config-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <Sparkles size={20} className="modal-icon-accent" />
            <h2 className="modal-title">Google Multi-Gen AI Suite</h2>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close AI Settings">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body-content">
          <p className="ai-modal-intro">
            Connect live Google models (Gemini 2.0 / 3.8 / Imagen 3) via your active Antigravity Tools session, Universal Model Bridge, Google OAuth, or direct Cloud keys for voice narration, 8K dark sci-fi concept art, and in-character lore telemetry.
          </p>

          {/* Section 0: Provider Gateway Selector */}
          <section className="settings-section">
            <div className="section-header">
              <Server size={16} className="section-icon" />
              <h3 className="section-title">Model Provider Gateway</h3>
            </div>
            <div className="options-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
              <button
                type="button"
                className={`option-btn ${config.providerMode === 'antigravity-tools' ? 'active' : ''}`}
                onClick={() =>
                  setConfig({
                    ...config,
                    providerMode: 'antigravity-tools',
                    proxyUrl: 'http://127.0.0.1:8045/v1',
                    proxyApiKey: config.proxyApiKey || 'sk-74fd5d2057004ec0ac2be093febdec65',
                    imageModel: 'antigravity-gemini-image',
                  })
                }
              >
                <span className="btn-option-name">Antigravity Tools</span>
                <span className="btn-option-sub">Port 8045 (Active App)</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.providerMode === 'universal-bridge' ? 'active' : ''}`}
                onClick={() =>
                  setConfig({
                    ...config,
                    providerMode: 'universal-bridge',
                    proxyUrl: 'http://localhost:8056/v1',
                    chatModel: 'gemini-3.8-flash',
                  })
                }
              >
                <span className="btn-option-name">Universal Bridge</span>
                <span className="btn-option-sub">Port 8056 (Zero-Auth)</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.providerMode === 'google-oauth' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, providerMode: 'google-oauth' })}
              >
                <span className="btn-option-name">Google OAuth</span>
                <span className="btn-option-sub">Bearer Token (ya29...)</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.providerMode === 'google-cloud-key' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, providerMode: 'google-cloud-key' })}
              >
                <span className="btn-option-name">Google API Key</span>
                <span className="btn-option-sub">Gemini API (AIza...)</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.providerMode === 'offline-dsp' ? 'active' : ''}`}
                onClick={() =>
                  setConfig({
                    ...config,
                    providerMode: 'offline-dsp',
                    voiceModel: 'web-speech-dsp',
                    imageModel: 'procedural-vector',
                  })
                }
              >
                <span className="btn-option-name">Atmospheric DSP</span>
                <span className="btn-option-sub">Local Offline Zero-Config</span>
              </button>
            </div>
          </section>

          {/* Section 1: Connection Credentials based on Mode */}
          {config.providerMode === 'antigravity-tools' && (
            <section className="settings-section">
              <div className="section-header">
                <ShieldCheck size={16} className="section-icon" />
                <h3 className="section-title">Antigravity Tools Proxy Configuration</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="setting-input-wrap">
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Proxy Base URL:</label>
                  <input
                    type="text"
                    value={config.proxyUrl}
                    onChange={(e) => setConfig({ ...config, proxyUrl: e.target.value.trim() })}
                    placeholder="http://127.0.0.1:8045/v1"
                    className="ai-api-input"
                  />
                </div>
                <div className="setting-input-wrap">
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Proxy Authorization Token:</label>
                  <input
                    type="password"
                    value={config.proxyApiKey}
                    onChange={(e) => setConfig({ ...config, proxyApiKey: e.target.value.trim() })}
                    placeholder="sk-..."
                    className="ai-api-input"
                  />
                  <span className="setting-hint">
                    Active key automatically read from your local Antigravity Tools window (.antigravity_tools/gui_config.json).
                  </span>
                </div>
              </div>
            </section>
          )}

          {config.providerMode === 'google-oauth' && (
            <section className="settings-section">
              <div className="section-header">
                <Key size={16} className="section-icon" />
                <h3 className="section-title">Google Cloud OAuth 2.0 Access Token</h3>
              </div>
              <div className="setting-input-wrap">
                <input
                  type="password"
                  placeholder="ya29.a0AcM612... (from 'gcloud auth print-access-token')"
                  value={config.oauthToken}
                  onChange={(e) => setConfig({ ...config, oauthToken: e.target.value.trim() })}
                  className="ai-api-input"
                />
                <span className="setting-hint">
                  Tokens generated via Google CLI OAuth session (alexharper1028@gmail.com). Kept strictly in memory/localStorage.
                </span>
              </div>
            </section>
          )}

          {config.providerMode === 'google-cloud-key' && (
            <section className="settings-section">
              <div className="section-header">
                <Key size={16} className="section-icon" />
                <h3 className="section-title">Google Cloud / Gemini API Key</h3>
              </div>
              <div className="setting-input-wrap">
                <input
                  type="password"
                  placeholder="AIzaSy... (leave blank for local atmospheric engine)"
                  value={config.apiKey}
                  onChange={(e) => setConfig({ ...config, apiKey: e.target.value.trim() })}
                  className="ai-api-input"
                />
                <span className="setting-hint">
                  Optional direct Google AI Studio API Key.
                </span>
              </div>
            </section>
          )}

          {/* Section 2: In-Character Telemetry & Chat Model */}
          <section className="settings-section">
            <div className="section-header">
              <Cpu size={16} className="section-icon" />
              <h3 className="section-title">Lore & Telemetry Model (Marcus Ward Analysis)</h3>
            </div>
            <div className="options-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <button
                type="button"
                className={`option-btn ${config.chatModel === 'gemini-3.8-flash' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, chatModel: 'gemini-3.8-flash' })}
              >
                <span className="btn-option-name">Gemini 3.8 Flash</span>
                <span className="btn-option-sub">Ultra-fast subharmonic reasoning</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.chatModel === 'gemini-3.1-pro-high' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, chatModel: 'gemini-3.1-pro-high' })}
              >
                <span className="btn-option-name">Gemini 3.1 Pro High</span>
                <span className="btn-option-sub">Deep philosophical & quantum lore</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.chatModel === 'gemini-2.5-flash' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, chatModel: 'gemini-2.5-flash' })}
              >
                <span className="btn-option-name">Gemini 2.5 Flash</span>
                <span className="btn-option-sub">Lightweight responsive telemetry</span>
              </button>
            </div>
          </section>

          {/* Section 3: Live Voice Narration Models */}
          <section className="settings-section">
            <div className="section-header">
              <Mic size={16} className="section-icon" />
              <h3 className="section-title">Voice Narration Engine</h3>
            </div>
            <div className="options-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
              <button
                type="button"
                className={`option-btn ${config.voiceModel === 'gemini-2.0-flash' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, voiceModel: 'gemini-2.0-flash' })}
              >
                <span className="btn-option-name">Gemini 2.0 Audio</span>
                <span className="btn-option-sub">Native Multimodal Voice</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.voiceModel === 'google-cloud-tts' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, voiceModel: 'google-cloud-tts' })}
              >
                <span className="btn-option-name">Journey TTS</span>
                <span className="btn-option-sub">DeepMind Audiobook</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.voiceModel === 'antigravity-gemini' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, voiceModel: 'antigravity-gemini' })}
              >
                <span className="btn-option-name">Gemini Voice Script</span>
                <span className="btn-option-sub">Gemini 3.8 + DSP Helmet</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.voiceModel === 'web-speech-dsp' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, voiceModel: 'web-speech-dsp' })}
              >
                <span className="btn-option-name">Atmospheric DSP</span>
                <span className="btn-option-sub">Local Offline Zero-Config</span>
              </button>
            </div>

            {/* Sub-selector for Gemini 2.0 Prebuilt Voices */}
            {config.voiceModel === 'gemini-2.0-flash' && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '10px',
                  background: 'rgba(0, 243, 255, 0.04)',
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 243, 255, 0.15)',
                }}
              >
                <span
                  style={{
                    fontSize: '0.78rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: 'var(--planck-cyan)',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  Gemini Voice Persona:
                </span>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px' }}>
                  {GEMINI_VOICES.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`option-btn ${config.geminiVoice === v.id ? 'active' : ''}`}
                      style={{ padding: '6px 8px', textAlign: 'left' }}
                      onClick={() => setConfig({ ...config, geminiVoice: v.id })}
                    >
                      <span className="btn-option-name" style={{ fontSize: '0.82rem' }}>
                        {v.name}
                      </span>
                      <span className="btn-option-sub" style={{ fontSize: '0.7rem' }}>
                        {v.role}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Helmet Comms Filter Checkbox */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '12px',
                cursor: 'pointer',
                fontSize: '0.82rem',
                color: '#cad5e2',
              }}
            >
              <input
                type="checkbox"
                checked={config.atmosphericFilter}
                onChange={(e) => setConfig({ ...config, atmosphericFilter: e.target.checked })}
                style={{ accentColor: 'var(--planck-cyan)' }}
              />
              <Radio size={14} color="var(--planck-cyan)" />
              <span>Route through USSC Geryon Pressurized Helmet Comms Filter (1400Hz Bandpass Web Audio DSP)</span>
            </label>
          </section>

          {/* Section 4: Image Generation Models */}
          <section className="settings-section">
            <div className="section-header">
              <Image size={16} className="section-icon" />
              <h3 className="section-title">Image Generation Engine</h3>
            </div>
            <div className="options-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
              <button
                type="button"
                className={`option-btn ${config.imageModel === 'antigravity-gemini-image' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, imageModel: 'antigravity-gemini-image' })}
              >
                <span className="btn-option-name">Gemini Flash Image</span>
                <span className="btn-option-sub">Antigravity Tools 8K SynthID Concept Art</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.imageModel === 'imagen-3.0-generate-002' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, imageModel: 'imagen-3.0-generate-002' })}
              >
                <span className="btn-option-name">Google Imagen 3</span>
                <span className="btn-option-sub">Hyper-detailed photorealistic matte paintings</span>
              </button>

              <button
                type="button"
                className={`option-btn ${config.imageModel === 'procedural-vector' ? 'active' : ''}`}
                onClick={() => setConfig({ ...config, imageModel: 'procedural-vector' })}
              >
                <span className="btn-option-name">Procedural Vector</span>
                <span className="btn-option-sub">Zero-latency SVG & Canvas dark sci-fi generator</span>
              </button>
            </div>
          </section>

          {/* Section 5: Cadence & Pitch Controls */}
          <section className="settings-section">
            <div className="section-header">
              <Sliders size={16} className="section-icon" />
              <h3 className="section-title">Narrator Demeanor & Cadence</h3>
            </div>
            <div className="slider-control-group">
              <div className="slider-label-row">
                <span>Reading Pace: {config.narratorPace.toFixed(2)}x</span>
                <span className="slider-sub">Pensive & Measured</span>
              </div>
              <input
                type="range"
                min="0.7"
                max="1.3"
                step="0.05"
                value={config.narratorPace}
                onChange={(e) => setConfig({ ...config, narratorPace: parseFloat(e.target.value) })}
                className="ai-range-slider"
              />
            </div>

            <div className="slider-control-group">
              <div className="slider-label-row">
                <span>Vocal Pitch: {config.narratorPitch.toFixed(2)}</span>
                <span className="slider-sub">Resonant Gravitas</span>
              </div>
              <input
                type="range"
                min="0.65"
                max="1.2"
                step="0.05"
                value={config.narratorPitch}
                onChange={(e) => setConfig({ ...config, narratorPitch: parseFloat(e.target.value) })}
                className="ai-range-slider"
              />
            </div>
          </section>

          {testStatus && (
            <div className="test-status-pill">
              <CheckCircle2 size={16} color="var(--planck-cyan)" />
              <span>{testStatus}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn-secondary" onClick={handleTestVoice}>
              Test Voice
            </button>
            <button
              className="btn-secondary"
              onClick={handleTestImage}
              disabled={isTestingImage}
            >
              {isTestingImage ? <RefreshCw size={14} className="spin" /> : null}
              <span>{isTestingImage ? 'Generating...' : 'Test Art'}</span>
            </button>
          </div>
          <button
            className="btn-primary-done"
            onClick={() => {
              handleSave();
              onClose();
            }}
          >
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
};
